import type { RouteLocation, RouteLocationRaw } from 'vue-router';
import { useRouter } from 'vue-router';
import { RouteNames } from '@/models/enums/RouteNames';
import { Role } from '@sysarb/sysarb-types/service-base/enums';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { computed } from 'vue';
import { usePackage, type UsePackagesReturn } from './usePackage';
import { storeToRefs } from 'pinia';
import { CustomizationInvolveViewIdentifier } from '@sysarb/sysarb-types/general/enums/customEmployment';

type RouterHelperReturn = {
    userTypeStartPageRoute: () => RouteLocation;
    userTypeMappingsRoute: () => string;
    userTypeGlobalOverviewRoute: () => string;
    userTypeCountryOverviewRoute: () => string;
    userTypeSalarySetRoute: () => string;
    userTypeSalarySetDashboardRoute: () => string;
    userTypeSalaryReviewAssessmentRoute: () => string;
    userTypeOnboardingRoute: () => string;
    userTypeAnalyzeRoute: () => string;
    userTypeAnalyzeV2Route: () => string;
    userTypeMappingsDashboardRoute: () => string;
    userTypeEqualJobRoute: () => string;
    userTypeJobArchitectureRoute: () => string;
    goBackwithFallback: (fallbackUrl: RouteLocationRaw) => void;
    managerDashboardRoute: () => string;
    employeeActivitiesDashboardRoute: () => string;
    employeeActivitiesDiscussionsRoute: () => string;
    employeeActivitiesDiscussionRoute: () => string;
    employeeActivitiesTargetsRoute: () => string;
    employeeActivitiesAssessmentsRoute: () => string;
};

export function getUserTypeStartPageRoute(
    currentUserRole: Role | null | undefined,
    packages: UsePackagesReturn | null = null
): RouteLocation {
    const newLocation = {} as RouteLocation;
    const { selectedInstance } = storeToRefs(useLoginStore());

    switch (currentUserRole) {
        case null:
        case undefined:
            newLocation.name = RouteNames.Login;
            break;
        case Role.Manager:
            newLocation.name = packages?.enableInvolvePortalManager.value === true ? RouteNames.ManagerDashboardV2 : RouteNames.NoModuleAccess;
            break;
        case Role.SectionManager:
            if (selectedInstance.value?.involveSettings?.customizationEmploymentViewIdentifier === CustomizationInvolveViewIdentifier.HIT_INVOLVE_001) {
                newLocation.name = RouteNames.SectionManagerStart;
                break;
            }
            newLocation.name = packages?.enableInvolvePortalManager.value === true ? RouteNames.SectionManagerDashboardV2 : RouteNames.NoModuleAccess;
            break;
        case Role.Administrator:
        case Role.Root:
        case Role.SystemAdministrator:
        case Role.Consultant:
                newLocation.name = RouteNames.AdministratorGlobalOverviewV2;
                return newLocation;
        case Role.SalarySurveyAnalyst:
                newLocation.name = RouteNames.AnalystGlobalOverviewV2;
                return newLocation;

        case Role.OperationalAdministrator:
                newLocation.name = RouteNames.OperationalAdministratorGlobalOverviewV2;
                return newLocation;
        case Role.ReadOnly:
                newLocation.name = RouteNames.ReadOnlyGlobalOverviewV2;
                return newLocation;
        case Role.Employee:
            if (packages?.payManagementOnly.value === true) {
                newLocation.name = RouteNames.EmployeeActivities;
            } else if (packages?.enableInvolvePortalEmployee.value !== true) {
                newLocation.name = RouteNames.NoModuleAccess;
            } else {
                const employeeViewIdentifier = selectedInstance.value?.involveSettings?.customizationEmploymentViewIdentifier;
                if (employeeViewIdentifier === CustomizationInvolveViewIdentifier.HIT_INVOLVE_001) {
                    newLocation.name = RouteNames.EmployeeCustomEmploymentDashboard;
                } else if (employeeViewIdentifier === CustomizationInvolveViewIdentifier.HIT_INVOLVE_DEAI_001) {
                    newLocation.name = RouteNames.EmployeeDeaiEmploymentDashboard;
                } else {
                    newLocation.name = RouteNames.EmployeeDashboard;
                }
            }
            break;
        case Role.Union:
            newLocation.name = RouteNames.UnionStart;
            break;
        default:
            newLocation.name = RouteNames.Login;
            console.info('RouterHelper', 'User role not found', currentUserRole);
            break;
    }

    return newLocation;
}

export function useRouterHelper(): RouterHelperReturn {
    /** Composables */
    const router = useRouter();

    const packages = usePackage();

    /** Stores */
    const { role } = storeToRefs(useLoginStore());

    /** Getters */
    const currentRole = computed<Role | null>(() => role.value?.role ?? null);

    function userTypeStartPageRoute(): RouteLocation {
        return getUserTypeStartPageRoute(currentRole.value, packages);
    }

    function userTypeMappingsRoute(): string {
        switch (currentRole.value) {
            case Role.Administrator:
            case Role.Root:
            case Role.SystemAdministrator:
            case Role.Consultant:
                return RouteNames.AdministratorPayEquityAuditOverview;
            case Role.OperationalAdministrator:
                return RouteNames.OperationalAdministratorMappings;
            case Role.SalarySurveyAnalyst:
                return RouteNames.AnalystMappings;
            case Role.ReadOnly:
                return RouteNames.ReadOnlyMappings;
            default:
                return RouteNames.Login;
        }
    }

    function userTypeGlobalOverviewRoute(): string {
        switch (currentRole.value) {
            case Role.Administrator:
            case Role.Root:
            case Role.SystemAdministrator:
            case Role.Consultant:
                return RouteNames.AdministratorGlobalOverviewV2;
            case Role.SalarySurveyAnalyst:
                return RouteNames.AnalystGlobalOverviewV2;
            case Role.OperationalAdministrator:
                return RouteNames.OperationalAdministratorGlobalOverviewV2;
            case Role.ReadOnly:
                return RouteNames.ReadOnlyGlobalOverviewV2;
            default:
                return RouteNames.Login;
        }
    }

    function userTypeCountryOverviewRoute(): string {
        return userTypeGlobalOverviewRoute();
    }

    function userTypeSalarySetRoute(): string {
        switch (currentRole.value) {
            case Role.Manager:
                return RouteNames.ManagerSalarySetV2;
            case Role.SectionManager:
                return RouteNames.SectionManagerSalarySet;
            case Role.Union:
                return RouteNames.UnionSalarySet;
            default:
                return RouteNames.Login;
        }
    }

    function userTypeSalarySetDashboardRoute(): string {
        switch (currentRole.value) {
            case Role.Manager:
                return RouteNames.ManagerSalarySetDashboardV2;
            case Role.SectionManager:
                return RouteNames.SectionManagerSalarySetDashboard;
            case Role.Union:
                return RouteNames.UnionSalarySetDashboard;
            default:
                return RouteNames.Login;
        }
    }

    function userTypeEqualJobRoute(): string {
        switch (currentRole.value) {
            case Role.Administrator:
            case Role.Root:
            case Role.SystemAdministrator:
            case Role.Consultant:
                return RouteNames.AdministratorPayEquityAuditAnalyzeWorksV2;
            case Role.OperationalAdministrator:
                return RouteNames.OperationalAdministratorPayEquityAuditAnalyzeWorksV2;
            case Role.SalarySurveyAnalyst:
                return RouteNames.AnalystPayEquityAuditAnalyzeWorksV2;
            case Role.ReadOnly:
                return RouteNames.ReadOnlyPayEquityAuditAnalyzeWorksV2;
            default:
                return RouteNames.Login;
        }
    }

    function userTypeSalaryReviewAssessmentRoute(): string {
        switch (currentRole.value) {
            case Role.Manager:
                return RouteNames.ManagerSalaryReviewAssessmentV2;
            default:
                return RouteNames.Login;
        }
    }

    /**
     * Return onboarding route base on user role
     * @return {string}
     */
    function userTypeOnboardingRoute(): string {
        switch (currentRole.value) {
            case Role.Administrator:
            case Role.Root:
            case Role.SystemAdministrator:
            case Role.Consultant:
                return RouteNames.AdministratorPayEquityAuditOnboarding;
            case Role.OperationalAdministrator:
                return RouteNames.OperationalAdministratorPayEquityAuditOnboarding;
            default:
                return RouteNames.Login;
        }
    }

    /**
     * Return mappings dashboard route base on user role and New PayEquityAudit feature flag
     * @return {string}
     */
    function userTypeMappingsDashboardRoute(): string {
        switch (currentRole.value) {
            case Role.Administrator:
            case Role.Root:
            case Role.SystemAdministrator:
            case Role.Consultant:
                return RouteNames.AdministratorPayEquityAuditDashboard;
            case Role.OperationalAdministrator:
                return RouteNames.OperationalAdministratorPayEquityAuditDashboard;
            case Role.SalarySurveyAnalyst:
                return RouteNames.AnalystPayEquityAuditDashboard;
            case Role.ReadOnly:
                return RouteNames.ReadOnlyPayEquityAuditDashboard;
            default:
                return RouteNames.Login;
        }
    }

    function userTypeAnalyzeRoute(): string {
        switch (currentRole.value) {
            case Role.Administrator:
            case Role.Root:
            case Role.SystemAdministrator:
            case Role.Consultant:
                return RouteNames.AdministratorMappingAnalyzeEqualWorks;
            case Role.OperationalAdministrator:
                return RouteNames.OperationalAdministratorMappingAnalyzeEqualWorks;
            case Role.SalarySurveyAnalyst:
                return RouteNames.AnalystMappingAnalyzeEqualWorks;
            case Role.ReadOnly:
                return RouteNames.ReadOnlyMappingAnalyzeEqualWorks;
            default:
                return RouteNames.Login;
        }
    }

    function userTypeAnalyzeV2Route(): string {
        switch (currentRole.value) {
            case Role.Administrator:
            case Role.Root:
            case Role.SystemAdministrator:
            case Role.Consultant:
                return RouteNames.AdministratorPayEquityAuditAnalyzeEqualJobsList;
            case Role.OperationalAdministrator:
                return RouteNames.OperationalAdministratorPayEquityAuditAnalyzeEqualJobsList;
            case Role.SalarySurveyAnalyst:
                return RouteNames.AnalystPayEquityAuditAnalyzeEqualJobsList;
            case Role.ReadOnly:
                return RouteNames.ReadOnlyPayEquityAuditAnalyzeEqualJobsList;
            default:
                return RouteNames.Login;
        }
    }

    function userTypeJobArchitectureRoute(): string {
        switch (currentRole.value) {
            case Role.Administrator:
            case Role.Root:
            case Role.SystemAdministrator:
            case Role.Consultant:
                return RouteNames.AdministratorJobArchitectureOverview;
            case Role.OperationalAdministrator:
                return RouteNames.OperationalAdministratorJobArchitectureOverview;
            case Role.SalarySurveyAnalyst:
                return RouteNames.AnalystJobArchitectureOverview;
            case Role.ReadOnly:
                return RouteNames.ReadOnlyJobArchitectureOverview;
            default:
                return RouteNames.Login;
        }
    }

    /**
     * Navigates back to the previous route if available, otherwise navigates to a fallback route.
     * @param {RouteLocationRaw} fallbackRoute - The route to navigate to if there is no previous route or if the previous route is the same as the current route.
     * @returns {void}
     */
    function goBackwithFallback(fallbackRoute: RouteLocationRaw): void {
        const fromRoute = router.options.history.state.back;
        const currentRoute = router.currentRoute.value.fullPath;

        if (fromRoute == null || (typeof fromRoute === 'string' && fromRoute.includes(currentRoute))) {
            void router.push(fallbackRoute);
        } else {
            router.back();
        }
    }

    /**
     * Return manager dashboard route
     * If the package is enabled and the feature flag is enabled, return the new manager dashboard route
     * Otherwise, return the old manager dashboard route
     *
     * @return {string}
     */
    function managerDashboardRoute(): string {
        return RouteNames.ManagerDashboardV2;
    }

    /**
     * The manager-employee-activities routes (discussions/targets/assessments) are shared
     * between the Manager and SectionManager route trees. These resolve the tree-appropriate
     * route name for the current role, so the shared page components don't need to know
     * which tree they're mounted under.
     */
    function employeeActivitiesDashboardRoute(): string {
        return currentRole.value === Role.SectionManager ? RouteNames.SectionManagerEmployeeDashboard : RouteNames.ManagerEmployeeDashboard;
    }

    function employeeActivitiesDiscussionsRoute(): string {
        return currentRole.value === Role.SectionManager ? RouteNames.SectionManagerEmployeeDiscussions : RouteNames.ManagerEmployeeDiscussions;
    }

    function employeeActivitiesDiscussionRoute(): string {
        return currentRole.value === Role.SectionManager ? RouteNames.SectionManagerEmployeeDiscussion : RouteNames.ManagerEmployeeDiscussion;
    }

    function employeeActivitiesTargetsRoute(): string {
        return currentRole.value === Role.SectionManager ? RouteNames.SectionManagerEmployeeTargets : RouteNames.ManagerEmployeeTargets;
    }

    function employeeActivitiesAssessmentsRoute(): string {
        return currentRole.value === Role.SectionManager ? RouteNames.SectionManagerEmployeeAssessments : RouteNames.ManagerEmployeeAssessments;
    }

    return {
        userTypeStartPageRoute,
        userTypeMappingsRoute,
        userTypeGlobalOverviewRoute,
        userTypeCountryOverviewRoute,
        userTypeSalarySetRoute,
        userTypeSalarySetDashboardRoute,
        userTypeSalaryReviewAssessmentRoute,
        userTypeOnboardingRoute,
        userTypeAnalyzeRoute,
        userTypeAnalyzeV2Route,
        userTypeMappingsDashboardRoute,
        userTypeEqualJobRoute,
        userTypeJobArchitectureRoute,
        goBackwithFallback,
        managerDashboardRoute,
        employeeActivitiesDashboardRoute,
        employeeActivitiesDiscussionsRoute,
        employeeActivitiesDiscussionRoute,
        employeeActivitiesTargetsRoute,
        employeeActivitiesAssessmentsRoute,
    };
}
